if (!customElements.get('product-inventory')) {
  class ProductInventory extends HTMLElement {
    constructor() {
      super();
      if (this.hidden) {
        this.initLazySection();
      } else {
        window.initLazyScript(this, this.initLazySection.bind(this));
      }
    }

    initLazySection() {
      this.inventoryNotice = this.querySelector('.js-inventory-notice');
      this.urgencyMessage = this.querySelector('.js-inventory-urgency');
      this.indicatorBar = this.querySelector('.js-inventory-indicator');
      this.variantInventory = this.getVariantInventory();
      this.productDeliveryDate = this.getProductDeliveryDate();

      // Bind to on:variant:change event for this product
      const variantPicker = this.closest('.product-info').querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      }

      // Init
      this.updateInventory(
        parseInt(this.dataset.inventoryQuantity, 10),
        this.dataset.variantAvailable === 'true',
        this.dataset.inventoryPolicy
      );
    }

    /**
     * Gets the inventory data for all product variants
     * @returns {?object}
     */
    getVariantInventory() {
      const dataEl = this.querySelector('[type="application/json"]');
      return this.variantInventory || JSON.parse(dataEl.textContent);
    }

    /**
     * Gets the product-level delivery date
     * @returns {?object}
     */
    getProductDeliveryDate() {
      const dataEl = this.querySelector('[data-product-delivery-date]');
      return dataEl ? JSON.parse(dataEl.textContent) : null;
    }

    /**
     * Handles a 'change' event on the variant picker element
     * @param {object} evt - Event object
     */
    handleVariantChange(evt) {
      if (evt.detail.variant) {
        const inventory = this.variantInventory.filter(
          (variant) => variant.id === evt.detail.variant.id
        )[0];
        this.updateInventory(
          inventory.inventory_quantity,
          inventory.available,
          inventory.inventory_policy,
          inventory
        );
      } else {
        this.updateInventory(0, false);
        this.hidden = true;
      }
    }

    /**
     *
     * Updates the inventory notice
     * @param {number} count - the inventory quantity available
     * @param {boolean} available - whether current variant is available
     * @param {string} inventoryPolicy - whether product continues selling when out of stock
     * @param {object} variantData - variant data including delivery dates
     */
    updateInventory(count, available, inventoryPolicy, variantData = null) {
      if (count === 0 && !available && this.hidden) {
        return;
      }

      let inventoryLevel;

      if (count <= 0) {
        if (inventoryPolicy === 'continue') {
          inventoryLevel = 'backordered';
        } else if (available) {
          inventoryLevel = 'in_stock';
        } else {
          inventoryLevel = 'none';
        }
      } else if (count <= parseInt(this.dataset.thresholdVeryLow, 10)) {
        inventoryLevel = 'very_low';
      } else if (count <= parseInt(this.dataset.thresholdLow, 10)) {
        inventoryLevel = 'low';
      } else {
        inventoryLevel = 'normal';
      }

      if (((this.dataset.showNotice === 'always'
        || (this.dataset.showNotice === 'low' && inventoryLevel.includes('low')))
        && !(inventoryLevel === 'backordered' && this.dataset.showNoStockBackordered === 'false'))) {
        this.hidden = false;

        // Set the inventory level data attribute
        this.setAttribute('data-inventory-level', inventoryLevel);

        // Determine whether to show the count or not
        if (inventoryLevel === 'backordered') {
          this.inventoryNotice.innerText = '';
        } else if (inventoryLevel !== 'in_stock' && (this.dataset.showCount === 'always' || (this.dataset.showCount === 'low' && inventoryLevel.includes('low')))) {
          this.inventoryNotice.innerText = theme.strings.onlyXLeft.replace('[quantity]', count);
        } else if (inventoryLevel === 'very_low') {
          this.inventoryNotice.innerText = theme.strings.veryLowStock;
        } else if (inventoryLevel === 'low') {
          this.inventoryNotice.innerText = theme.strings.lowStock;
        } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
          this.inventoryNotice.innerText = theme.strings.inStock;
        } else if (inventoryLevel === 'none') {
          this.inventoryNotice.innerText = theme.strings.noStock;
        }

        // Update urgency message if needed
        if (this.urgencyMessage) {
          if (inventoryLevel === 'very_low') {
            this.urgencyMessage.innerHTML = this.dataset.textVeryLow;
          } else if (inventoryLevel === 'low') {
            this.urgencyMessage.innerHTML = this.dataset.textLow;
          } else if (inventoryLevel === 'backordered') {
            // Show custom delivery date for backorders if available
            const deliveryText = this.getDeliveryText(variantData);
            if (deliveryText) {
              this.urgencyMessage.innerHTML = deliveryText;
            } else {
              this.urgencyMessage.innerHTML = this.dataset.textNoStockBackordered;
            }
          } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
            this.urgencyMessage.innerHTML = this.dataset.textNormal;
          } else if (inventoryLevel === 'none') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStock;
          }
        }

        // Update the bar indicator
        if (this.indicatorBar) {
          this.indicatorBar.hidden = false;

          let newWidth;
          if ((count >= this.dataset.scale || inventoryLevel === 'in_stock') && inventoryLevel !== 'backordered') {
            newWidth = 100;
          } else {
            newWidth = ((100 / parseInt(this.dataset.scale, 10)) * count).toFixed(1);
          }
          this.indicatorBar.querySelector('span').style.width = `${newWidth}%`;
        }
      } else {
        this.hidden = true;
      }
    }

    /**
     * Gets the delivery text for the current variant or product
     * @param {object} variantData - variant data including delivery dates
     * @returns {string} Formatted delivery text or null
     */
    getDeliveryText(variantData) {
      let deliveryDate = null;
      let startDate = null;
      let endDate = null;

      // Check variant delivery dates first
      if (variantData) {
        deliveryDate = variantData.delivery_date;
        startDate = variantData.delivery_date_start;
        endDate = variantData.delivery_date_end;
      }

      // Fallback to product delivery dates
      if (!deliveryDate && !startDate && !endDate && this.productDeliveryDate) {
        deliveryDate = this.productDeliveryDate.delivery_date;
        startDate = this.productDeliveryDate.delivery_date_start;
        endDate = this.productDeliveryDate.delivery_date_end;
      }

      // Priority: date range first, then single date
      if (startDate && endDate) {
        return this.formatDateRange(startDate, endDate);
      } else if (deliveryDate) {
        return this.formatSingleDate(deliveryDate);
      }

      return null;
    }

    /**
     * Formats a single delivery date in Danish format
     * @param {string} dateString - ISO date string
     * @returns {string} Formatted delivery text
     */
    formatSingleDate(dateString) {
      if (!dateString) return null;

      const danishMonths = [
        'januar', 'februar', 'marts', 'april', 'maj', 'juni',
        'juli', 'august', 'september', 'oktober', 'november', 'december'
      ];

      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const day = date.getDate();
        const month = danishMonths[date.getMonth()];
        return `<p>Levering: ${day}. ${month}</p>`;
      }

      return null;
    }

    /**
     * Formats a date range in Danish format
     * @param {string} startDateString - ISO start date string
     * @param {string} endDateString - ISO end date string
     * @returns {string} Formatted delivery range text
     */
    formatDateRange(startDateString, endDateString) {
      if (!startDateString || !endDateString) return null;

      const danishMonths = [
        'januar', 'februar', 'marts', 'april', 'maj', 'juni',
        'juli', 'august', 'september', 'oktober', 'november', 'december'
      ];

      const startDate = new Date(startDateString);
      const endDate = new Date(endDateString);
      
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
        const startDay = startDate.getDate();
        const endDay = endDate.getDate();
        const startMonth = danishMonths[startDate.getMonth()];
        const endMonth = danishMonths[endDate.getMonth()];
        
        // Same month range: "mellem 1-2. juli"
        if (startDate.getMonth() === endDate.getMonth()) {
          return `<p>Levering: mellem ${startDay}-${endDay}. ${startMonth}</p>`;
        } else {
          // Different months: "mellem 30. juni - 2. juli"
          return `<p>Levering: mellem ${startDay}. ${startMonth} - ${endDay}. ${endMonth}</p>`;
        }
      }

      return null;
    }

    /**
     * Formats delivery date(s) in Danish format
     * @param {string} dateString - ISO date string or date range
     * @returns {string} Formatted delivery text
     * @deprecated Use getDeliveryText instead
     */
    formatDeliveryDate(dateString) {
      if (!dateString) return '';

      const danishMonths = [
        'januar', 'februar', 'marts', 'april', 'maj', 'juni',
        'juli', 'august', 'september', 'oktober', 'november', 'december'
      ];

      // Check if it's a date range (contains comma, dash, or 'til'/'to')
      const rangeSeparators = [',', ' - ', '-', ' til ', ' to ', ' / ', '/'];
      let isRange = false;
      let separator = '';
      
      for (const sep of rangeSeparators) {
        if (dateString.includes(sep)) {
          isRange = true;
          separator = sep;
          break;
        }
      }

      if (isRange) {
        const dates = dateString.split(separator).map(d => d.trim());
        if (dates.length === 2) {
          const startDate = new Date(dates[0]);
          const endDate = new Date(dates[1]);
          
          if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
            const startDay = startDate.getDate();
            const endDay = endDate.getDate();
            const startMonth = danishMonths[startDate.getMonth()];
            const endMonth = danishMonths[endDate.getMonth()];
            
            // Same month range: "mellem 1-2 juli"
            if (startDate.getMonth() === endDate.getMonth()) {
              return `<p>Levering: mellem ${startDay}-${endDay}. ${startMonth}</p>`;
            } else {
              // Different months: "mellem 30. juni - 2. juli"
              return `<p>Levering: mellem ${startDay}. ${startMonth} - ${endDay}. ${endMonth}</p>`;
            }
          }
        }
      }

      // Single date: "Levering: 1. juli"
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const day = date.getDate();
        const month = danishMonths[date.getMonth()];
        return `<p>Levering: ${day}. ${month}</p>`;
      }

      // Fallback to original backorder text if date parsing fails
      return this.dataset.textNoStockBackordered;
    }
  }

  customElements.define('product-inventory', ProductInventory);
}
